import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/sv';
import 'dayjs/locale/en';
import calendar from 'dayjs/plugin/calendar';
import { Button } from 'react-bootstrap';
import { IconButton } from '@velove/design-system';
import { useUpdateDeliveryWindow } from './hooks/useUpdateDeliveryWindow';
import { ETA } from '../../contexts/contextTypes';

interface DeliveryWindowReschedulerProps {
  packageId: string;
  taskId: string;
  eta: ETA;
  deliveryWindows: { start: Date; end: Date }[];
  canReschedule: boolean;
}

const DeliveryWindowRescheduler = ({ packageId, taskId, eta, deliveryWindows, canReschedule }: DeliveryWindowReschedulerProps) => {
  const { t, i18n } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedWindow, setSelectedWindow] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateDeliveryWindowMutation = useUpdateDeliveryWindow();

  dayjs.extend(calendar);
  dayjs.locale(i18n.language);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const getCurrentEtaValue = () => {
    if (eta?.lowerBound && eta?.upperBound) {
      return JSON.stringify({
        start: eta.lowerBound,
        end: eta.upperBound
      });
    }
    return '';
  };

  const formatDeliveryDate = (date: Date) => {
    const etaDate = dayjs(date);
    const today = dayjs();

    if (etaDate.isSame(today, 'day')) {
      return `${t('delivery_today')}, `;
    } else if (etaDate.isSame(today.subtract(1, 'day'), 'day')) {
      return `${t('delivery_yesterday')}, `;
    } else if (etaDate.isSame(today.add(1, 'day'), 'day')) {
      return `${t('delivery_tomorrow')}, `;
    } else if (etaDate.isBefore(today, 'day')) {
      return `${etaDate.format('YYYY-MM-DD')}, `;
    } else {
      return `${etaDate.format('dddd')}, `;
    }
  };

  const formatDeliveryWindow = (window: { start: Date; end: Date }) => {
    const startTime = dayjs(window.start).format('HH:mm');
    const endTime = dayjs(window.end).format('HH:mm');
    const date = formatDeliveryDate(window.start);
    return `${date}${startTime} - ${endTime}`;
  };

  const handleReschedule = async () => {
    if (selectedWindow && packageId && taskId) {
      setErrorMessage('');

      try {
        const window = JSON.parse(selectedWindow);

        await updateDeliveryWindowMutation.mutateAsync({
          packageId,
          payload: {
            taskId,
            deliveryWindow: {
              start: new Date(window.start).toISOString(),
              end: new Date(window.end).toISOString()
            }
          }
        });

        setIsEditing(false);
        setSelectedWindow('');
        setIsDropdownOpen(false);
        console.log('Delivery window successfully updated');
      } catch (error: any) {
        const errorMsg = error?.response?.data?.message || 'Failed to update delivery window. Please try again.';
        setErrorMessage(errorMsg);
        console.error('Error updating delivery window:', errorMsg);
      }
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSelectedWindow('');
    setIsDropdownOpen(false);
    setErrorMessage('');
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: 20 }}>
        {!isEditing ? (
          <>
            <h3
              id="eta-display"
              className="eta-display-text"
              style={{
                marginBottom: 0
              }}
            >
              {formatDeliveryDate(eta?.lowerBound || new Date())}
              {dayjs(eta?.lowerBound || new Date(Date.now() - 5 * 60 * 1000)).format('HH:mm') +
                ' - ' +
                dayjs(eta?.upperBound || new Date(Date.now() + 5 * 60 * 1000)).format('HH:mm')}
            </h3>
            {canReschedule && (
              <IconButton
                id="reschedule-button"
                icon={'Edit'}
                sx={{
                  height: '40px',
                  width: '40px',
                  padding: '4px',
                  color: 'white',
                  borderRadius: '100%',
                  borderStyle: 'solid',
                  borderWidth: '2px',
                  backgroundColor: 'var(--container-bg-color)',
                  borderColor: 'white'
                }}
                onClick={() => {
                  setIsEditing(true);
                  setSelectedWindow(getCurrentEtaValue());
                  setErrorMessage('');
                }}
              />
            )}
          </>
        ) : (
          <>
            <div ref={dropdownRef} className="custom-dropdown-wrapper">
              <div id="eta-display" className="custom-dropdown-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                <span>
                  {selectedWindow
                    ? (() => {
                        const window = JSON.parse(selectedWindow);
                        return formatDeliveryWindow(window);
                      })()
                    : formatDeliveryDate(eta?.lowerBound || new Date()) +
                      dayjs(eta?.lowerBound || new Date(Date.now() - 5 * 60 * 1000)).format('HH:mm') +
                      ' - ' +
                      dayjs(eta?.upperBound || new Date(Date.now() + 5 * 60 * 1000)).format('HH:mm')}
                </span>
                <span style={{ marginLeft: '8px' }}>▼</span>
              </div>
              {isDropdownOpen && (
                <div className="custom-dropdown-menu">
                  <div
                    className={`custom-dropdown-item ${selectedWindow === getCurrentEtaValue() ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedWindow(getCurrentEtaValue());
                      setIsDropdownOpen(false);
                    }}
                  >
                    {formatDeliveryDate(eta?.lowerBound || new Date())}
                    {dayjs(eta?.lowerBound || new Date(Date.now() - 5 * 60 * 1000)).format('HH:mm') +
                      ' - ' +
                      dayjs(eta?.upperBound || new Date(Date.now() + 5 * 60 * 1000)).format('HH:mm')}
                  </div>
                  {deliveryWindows?.map((window, index) => {
                    const windowValue = JSON.stringify(window);
                    return (
                      <div
                        key={index}
                        className={`custom-dropdown-item ${selectedWindow === windowValue ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedWindow(windowValue);
                          setIsDropdownOpen(false);
                        }}
                      >
                        {formatDeliveryWindow(window)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <Button
              id="confirm-reschedule-button"
              className="reschedule-confirm-button"
              onClick={handleReschedule}
              disabled={!selectedWindow || selectedWindow === getCurrentEtaValue() || updateDeliveryWindowMutation.isLoading}
            >
              {updateDeliveryWindowMutation.isLoading ? '⏳' : '✓'}
            </Button>
            <Button id="cancel-reschedule-button" className="reschedule-cancel-button" onClick={handleCancelEdit}>
              ✕
            </Button>
          </>
        )}
      </div>
      {errorMessage && (
        <div
          style={{
            padding: '10px',
            marginBottom: 20,
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c33'
          }}
        >
          {errorMessage}
        </div>
      )}
    </>
  );
};

export default DeliveryWindowRescheduler;
